/**
 * AdminTokenUsage Component
 *
 * Container component for admin token usage analytics.
 * Two views:
 * - List view: Platform summary + user ranking table with search & pagination
 * - Detail view: Single user's full UsageDashboard
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import apiService from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import AdminPlatformSummaryCards from './AdminPlatformSummaryCards';
import AdminUserUsageTable from './AdminUserUsageTable';
import type { SortField } from './AdminUserUsageTable';
import UsageDashboard from '../TokenUsage/UsageDashboard';
import type {
  TokenUsageTimePeriod,
  AdminPlatformUsageSummary,
  AdminUserUsageSummary,
} from '../../types/tokenUsage';

const PERIOD_OPTIONS: { value: TokenUsageTimePeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_year', label: 'This Year' },
  { value: 'all_time', label: 'All Time' },
];

const PAGE_SIZE = 20;

const AdminTokenUsage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<TokenUsageTimePeriod>('this_month');
  const [selectedUser, setSelectedUser] = useState<AdminUserUsageSummary | null>(null);
  const [sortBy, setSortBy] = useState<SortField>('total_tokens');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Platform summary state
  const [platformSummary, setPlatformSummary] = useState<AdminPlatformUsageSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // User list state
  const [userList, setUserList] = useState<AdminUserUsageSummary[]>([]);
  const [userTotal, setUserTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);

  // Debounced search value (only updates after user stops typing)
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const fetchData = useCallback(async () => {
    const params = { period: selectedPeriod };
    const skip = (currentPage - 1) * PAGE_SIZE;

    setSummaryLoading(true);
    setSummaryError(null);
    setUsersLoading(true);
    setUsersError(null);

    const [summaryResult, usersResult] = await Promise.allSettled([
      apiService.getAdminUsageSummary(params),
      apiService.getAdminUsageByUser({
        ...params,
        sort_by: sortBy,
        search: debouncedSearch || undefined,
        skip,
        limit: PAGE_SIZE,
      }),
    ]);

    if (summaryResult.status === 'fulfilled') {
      setPlatformSummary(summaryResult.value);
    } else {
      setSummaryError('Failed to load platform summary');
    }
    setSummaryLoading(false);

    if (usersResult.status === 'fulfilled') {
      setUserList(usersResult.value.users);
      setUserTotal(usersResult.value.total);
    } else {
      setUsersError('Failed to load user usage data');
    }
    setUsersLoading(false);
  }, [selectedPeriod, sortBy, debouncedSearch, currentPage]);

  useEffect(() => {
    if (!selectedUser) {
      fetchData();
    }
  }, [fetchData, selectedUser]);

  // Reset to page 1 when search, sort, or period changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortBy, selectedPeriod]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(query);
    }, 300);
  };

  const handleUserClick = (user: AdminUserUsageSummary) => {
    setSelectedUser(user);
  };

  const handleBack = () => {
    setSelectedUser(null);
  };

  const handleSortChange = (field: SortField) => {
    setSortBy(field);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const isAnyLoading = summaryLoading || usersLoading;
  const totalPages = Math.max(1, Math.ceil(userTotal / PAGE_SIZE));

  // Detail view: show user's full UsageDashboard
  if (selectedUser) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            data-testid="back-to-list-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Users
          </button>
        </div>
        <UsageDashboard
          userId={selectedUser.user_id}
          readOnly
          headerTitle={`Usage for ${selectedUser.username}`}
          headerSubtitle={selectedUser.email}
        />
      </div>
    );
  }

  // List view: platform summary + user ranking table
  return (
    <div className="space-y-6" data-testid="admin-token-usage">
      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-warm-white">Platform Token Usage</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Monitor token consumption across all users
          </p>
        </div>

        <div className="flex items-center gap-3">
          <CustomSelect
            value={selectedPeriod}
            onChange={(value) => setSelectedPeriod(value as TokenUsageTimePeriod)}
            options={PERIOD_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
            aria-label="Period"
          />

          <button
            data-testid="admin-refresh-button"
            onClick={fetchData}
            disabled={isAnyLoading}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-warm-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${isAnyLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Platform Summary Cards */}
      <AdminPlatformSummaryCards
        summary={platformSummary}
        loading={summaryLoading}
        error={summaryError}
      />

      {/* User Ranking Table */}
      <AdminUserUsageTable
        users={userList}
        total={userTotal}
        isLoading={usersLoading}
        error={usersError}
        onUserClick={handleUserClick}
        sortBy={sortBy}
        onSortChange={handleSortChange}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default AdminTokenUsage;
